const ERROR_REPORTING_FLAG = "__equipErrorReportingInstalled";
const FETCH_REPORTING_FLAG = "__equipFetchReportingInstalled";
const MAX_TRACKED_FETCHES = 20;

const trackedFetches = [];

const pushTrackedFetch = (request) => {
    trackedFetches.push(request);

    if (trackedFetches.length > MAX_TRACKED_FETCHES) {
        trackedFetches.shift();
    }
};

const redactValue = (key, value) =>
    /authorization|password|token|secret|session|auth/i.test(key) ? "[redacted]" : value;

const redactUrl = (url) => {
    try {
        const parsedUrl = new URL(url, window.location.origin);
        parsedUrl.searchParams.forEach((value, key) => {
            parsedUrl.searchParams.set(key, redactValue(key, value));
        });

        return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
    } catch (e) {
        return String(url);
    }
};

const safeJson = (value) => {
    try {
        return JSON.stringify(value, null, 2);
    } catch (e) {
        return String(value);
    }
};

const formatKeyValues = (values) =>
    Object.entries(values)
        .filter(([, value]) => value !== undefined && value !== null && value !== "")
        .map(([key, value]) => `${key}: ${String(value)}`)
        .join("\n");

const getFetchInputUrl = (input) => {
    if (typeof input === "string") {
        return input;
    }

    return input?.url || String(input);
};

const getFetchMethod = (input, init) => init?.method || input?.method || "GET";

const getObjectDetails = (value) => {
    if (!value || typeof value !== "object") {
        return {};
    }

    const details = {
        constructor: value.constructor?.name,
        toString: Object.prototype.toString.call(value),
    };

    for (const key of Object.keys(value)) {
        details[key] = redactValue(key, value[key]);
    }

    return details;
};

const formatErrorLike = (error) => {
    if (!error || typeof error !== "object") {
        return String(error);
    }

    const details = {
        constructor: error.constructor?.name,
        name: error.name,
        message: error.message,
        stack: error.stack,
        cause: error.cause ? formatErrorLike(error.cause) : undefined,
        code: error.code,
        status: error.status,
        statusText: error.statusText,
        url: error.url,
    };

    for (const key of Object.keys(error)) {
        if (details[key] === undefined) {
            details[key] = redactValue(key, error[key]);
        }
    }

    return formatKeyValues(details) || safeJson(error);
};

const formatPromiseDetails = (promise) => {
    if (!promise) {
        return "not available";
    }

    return formatKeyValues(getObjectDetails(promise)) || Object.prototype.toString.call(promise);
};

const formatUnhandledReason = (reason) => {
    const reasonType = reason === null ? "null" : typeof reason;
    const reasonDetails = [
        formatKeyValues({
            reasonType,
            reasonConstructor: reason?.constructor?.name,
            reasonToString: reason && typeof reason === "object" ? Object.prototype.toString.call(reason) : undefined,
        }),
    ];

    if (reason instanceof Error) {
        reasonDetails.push(formatErrorLike(reason));
        return reasonDetails.filter(Boolean).join("\n\n");
    }

    if (typeof reason === "string") {
        reasonDetails.push(reason);
        return reasonDetails.filter(Boolean).join("\n\n");
    }

    if (reason && typeof reason === "object") {
        reasonDetails.push(formatErrorLike(reason));
        reasonDetails.push(`rawReason: ${safeJson(getObjectDetails(reason))}`);
        return reasonDetails.filter(Boolean).join("\n\n");
    }

    reasonDetails.push(String(reason));
    return reasonDetails.filter(Boolean).join("\n\n");
};

const formatRecentFetches = () =>
    trackedFetches.length ? `\n\nrecent fetch requests:\n${safeJson(trackedFetches)}` : "";

const sendErrorReport = (details) => {
    const uri = window.location?.href || "unknown";
    const detailsWithUri = `uri: ${uri}\n\n${details}${formatRecentFetches()}`;

    fetch("/error/log", {
        method: "POST",
        headers: {
            "Content-Type": "application/json; charset=utf-8",
        },
        body: JSON.stringify({
            context: navigator.userAgent,
            details: detailsWithUri,
        }),
        keepalive: true,
    }).catch(() => {});
};

const registerFetchTracking = () => {
    if (window[FETCH_REPORTING_FLAG] || typeof window.fetch !== "function") {
        return;
    }
    window[FETCH_REPORTING_FLAG] = true;

    const originalFetch = window.fetch.bind(window);

    window.fetch = (input, init) => {
        const startedAt = new Date();
        const request = {
            at: startedAt.toISOString(),
            method: getFetchMethod(input, init),
            url: redactUrl(getFetchInputUrl(input)),
            status: "pending",
        };

        pushTrackedFetch(request);

        return originalFetch(input, init).then(
            (response) => {
                request.status = response.status;
                request.statusText = response.statusText;
                request.ok = response.ok;
                request.durationMs = Date.now() - startedAt.getTime();
                return response;
            },
            (error) => {
                request.status = "rejected";
                request.error = formatErrorLike(error);
                request.durationMs = Date.now() - startedAt.getTime();
                throw error;
            }
        );
    };
};

export const registerGlobalErrorReporting = () => {
    if (window[ERROR_REPORTING_FLAG]) {
        return;
    }
    window[ERROR_REPORTING_FLAG] = true;
    registerFetchTracking();

    const onErrorEvent = (event) => {
        const details =
            `${event.filename || "unknown"}:${event.lineno || 0} (${event.colno || 0})\n\n` +
            `${String(event.message || "Unknown error")}` +
            (event.error ? `\n\n${formatErrorLike(event.error)}` : "");

        sendErrorReport(details);
    };

    const onUnhandledRejection = (event) => {
        const details = [
            "unhandledrejection",
            "",
            "Promise",
            "-------",
            formatPromiseDetails(event.promise),
            "",
            "Reason",
            "------",
            formatUnhandledReason(event.reason),
        ].join("\n");
        sendErrorReport(details);
    };

    // Capture phase makes this more reliable when frameworks/dev tooling intercept errors.
    window.addEventListener("error", onErrorEvent, true);
    window.addEventListener("unhandledrejection", onUnhandledRejection);

    // Fallback for environments that rely on onerror hooks.
    const previousOnError = window.onerror;
    window.onerror = function (msg, url, line, columnNo, error) {
        onErrorEvent({
            message: msg,
            filename: url,
            lineno: line,
            colno: columnNo,
            error,
        });

        if (typeof previousOnError === "function") {
            return previousOnError(msg, url, line, columnNo, error);
        }

        return false;
    };
};
